import Loading from 'Components/Loading.component'
import { getInitialSearchPath } from 'Lib/AppUtils'
import { ActionType } from 'Reducers'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDispatch } from 'store'

// Past startUpSaga's 30s long-path timeout and the one reload it retries with.
const RESOLVE_STALL_TIMEOUT = 45000

const Wrapper = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${(props) => props.theme.spacing.large};
`

const Content = styled.div`
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing.medium};
`

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.grey900};
`

const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
`

const RetryButton = styled.button`
  margin-top: ${(props) => props.theme.spacing.small};
  padding: 14px 32px;
  border: none;
  border-radius: 30px;
  background: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.basicWhite};
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.colors.primaryDark};
  }
`

// Where the MoR ebook checkout returns a paying buyer: resolve the purchase, then move
// the funnel on. The offer page is the cancel target only.
const EbookPurchaseSuccessContainer: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [resolveStalled, setResolveStalled] = useState(false)

  useEffect(() => {
    // Read from this page load's query string: sanitizeUrl strips the id from the live
    // URL, and the persisted store would resurrect it on a later visit.
    const checkoutSessionId = new URLSearchParams(getInitialSearchPath()).get('ebook_session_id') ?? undefined

    if (!checkoutSessionId) {
      logEvent('web_morEbookReturnWithoutSession')
    }

    dispatch({ type: ActionType.RESOLVE_MOR_EBOOK_PURCHASE, checkoutSessionId })

    // A completed resolve navigates away, so firing while still mounted means startup
    // or auth restore never landed. Only a reload re-runs them.
    const stallTimer = setTimeout(() => {
      logEvent('web_morEbookResolveTimeout', checkoutSessionId ? { checkoutSessionId } : {})
      setResolveStalled(true)
    }, RESOLVE_STALL_TIMEOUT)

    return () => clearTimeout(stallTimer)
  }, [])

  if (resolveStalled) {
    return (
      <Wrapper>
        <Content>
          <Title>{t('ebookPurchaseSuccess.stalledTitle')}</Title>
          <Description>{t('ebookPurchaseSuccess.stalledDescription')}</Description>
          <RetryButton onClick={() => window.location.reload()}>
            {t('ebookPurchaseSuccess.stalledRetryButton')}
          </RetryButton>
        </Content>
      </Wrapper>
    )
  }

  return (
    <Loading />
  )
}

export default EbookPurchaseSuccessContainer
